import React, { useState, useEffect, lazy, Suspense } from 'react';
import Grid from '@mui/material/GridLegacy';
import { responsiveFontSize, responsiveIconSize, fontSizeFixed, iconSizeFixed } from '../util/responsiveUtils';
import {
    Box,
    Container,
    Typography,
    TextField,
    InputAdornment,
    Paper,
    useTheme,
    useMediaQuery,
    Autocomplete,
    alpha,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ContentCard, CustomSnackbar } from '../components/common';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BuildIcon from '@mui/icons-material/Build';
import BoltIcon from '@mui/icons-material/Bolt';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import YardIcon from '@mui/icons-material/Yard';
import HandymanIcon from '@mui/icons-material/Handyman';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SecurityIcon from '@mui/icons-material/Security';
import { Z_INDEX } from '../constants/zIndex';
import { useLocation } from '../context/LocationContext';
import { useNavigate, useLocation as useRouterLocation } from 'react-router';
import { scheduleDeferred } from '../util/scheduleDeferred';
import { dismissHomePageBootLoader } from '../components/common/HomePageSkeleton';
import { useAuth } from '../context';
import { getIconColor, getButtonColor } from '../theme/themeUtils';
import { DraggableBottomSheet } from '../components/DraggableBottomSheet';
import { categoryKeyToPath } from '../util/pathUtils';
import { CustomButton } from '../components/common/CustomButton';
import iPhoneLogoDark from '../assets/images/iPhone-logo-dark.webp';
import iPhoneLogoLight from '../assets/images/iPhone-logo-light.webp';
import howItWorksStep1 from '../assets/images/step1.webp';
import howItWorksStep2 from '../assets/images/step2.webp';
import howItWorksStep3 from '../assets/images/step3.webp';
import howItWorksStep4 from '../assets/images/step4.webp';
import expertSignup from '../assets/images/expert-signup.webp';
import {
    APP_STORE_URL,
    PLAY_STORE_URL,
} from '../util/platformUtils';
import { useHeroSearchServices, type HeroSearchServiceOption } from '../hooks/useHeroSearchServices';
import Groups2Icon from '@mui/icons-material/Groups2';
import { displayNameToDatabaseCategory } from '../data/serviceCategoryDbMapping';

/** Reserve height for below-the-fold homepage sections while they defer on mobile. */

const CitySelectionModal = lazy(() =>
    import('../components/CitySelectionModal').then((m) => ({ default: m.CitySelectionModal })),
);
const AuthModal = lazy(() =>
    import('../components/loginAndAuth/AuthModal').then((m) => ({ default: m.AuthModal })),
);
const GetStartedChooser = lazy(() =>
    import('../components/loginAndAuth/GetStartedChooser').then((m) => ({ default: m.GetStartedChooser })),
);
const HowItWorksModal = lazy(() =>
    import('../components/footer/HowItWorksModal').then((m) => ({ default: m.HowItWorksModal })),
);

const serviceCategories = [
    { name: 'Plumbing', icon: PlumbingIcon, color: '#4FC3F7', category: 'Handyman & Repairs', subcategory: 'Plumbing', description: 'Fixes, installs & repairs' },
    { name: 'Electrical', icon: BoltIcon, color: '#FFD54F', category: 'Handyman & Repairs', subcategory: 'Electrical', description: 'Wiring, panels & fixtures' },
    { name: 'Cleaning', icon: CleaningServicesIcon, color: '#26A69A', category: 'Cleaning Services', subcategory: 'Home Cleaning', description: 'Deep & regular cleaning' },
    { name: 'Auto Repair', icon: DirectionsCarIcon, color: '#5C6BC0', category: 'Auto Services', subcategory: 'Car Repair', description: 'Maintenance & repairs' },
    { name: 'Yard & Outdoor', icon: YardIcon, color: '#66BB6A', category: 'Seasonal & Outdoor Maintenance', subcategory: 'Landscaping', description: 'Landscaping & maintenance' },
    { name: 'Assembly', icon: HandymanIcon, color: '#FF8A65', category: 'Assembly and Installation', subcategory: 'Furniture Assembly', description: 'Furniture & equipment' },
    { name: 'Delivery', icon: LocalShippingIcon, color: '#BA68C8', category: 'Shopping and Delivery', subcategory: 'Item Delivery', description: 'Pickup & delivery' },
    { name: 'Home Improvement', icon: BuildIcon, color: '#4DD0E1', category: 'Home Improvement and Renovation', subcategory: 'General Contracting', description: 'Renovations & upgrades' },
];

const HOW_IT_WORKS_STEP_IMAGES = [howItWorksStep1, howItWorksStep2, howItWorksStep3, howItWorksStep4] as const;

const HOW_IT_WORKS_STEP_ALTS = [
    'Step 1: Post your home service project with photos and schedule details on Virtoly',
    'Step 2: Get quotes from local experts — compare bids and service proposals',
    'Step 3: Hire a verified local expert based on reviews, ratings, and badges',
    'Step 4: Relax while your expert completes the job with secure Virtoly payments',
] as const;

const howItWorksSteps = [
    { step: '1', title: 'Post', subtitle: 'Your Project', desc: 'Describe your home service need with photos and set your preferred schedule' },
    { step: '2', title: 'Get', subtitle: 'Quotes', desc: 'Create auctions to get the lowest bid or post projects to receive quotes from local experts' },
    { step: '3', title: 'Hire', subtitle: 'Your Expert', desc: 'Choose a vetted local expert based on reviews, ratings, and verified badges' },
    { step: '4', title: 'Relax', subtitle: '& Enjoy', desc: 'Get it done with Virtoly protection and secure payments' },
];

/** Shortest path on the ring for carousel slide direction (dot jumps). */
function shortestHowItWorksDirection(from: number, to: number, length: number): 1 | -1 {
    if (from === to) return 1;
    const forward = (to - from + length) % length;
    const backward = (from - to + length) % length;
    return forward <= backward ? 1 : -1;
}

const HERO_SERVICE_FIELD_PLACEHOLDER = 'Select a service';
const HERO_ALL_SERVICES_OPTION_LABEL = 'All services';

const CONTAINER_PX = { xs: 2, sm: 3, md: 4 } as const;
const SECTION_PY = { xs: 3, sm: 4, md: 5 } as const;
/** Mobile “All Services” carousel tile — icon + glow (matches hero yellow accent). */
const ALL_SERVICES_ACCENT = '#FFD54F';

/** Glass-style tiles on the homepage gradient (blur + translucent fill + soft edge). */
function getHomePageGlassTileSx(theme: Theme) {
    const dark = theme.palette.mode === 'dark';
    return {
        backdropFilter: 'blur(12px) saturate(1.08)',
        background: dark ? alpha(theme.palette.background.paper, 0.82) : alpha('#fff', 0.94),
        border: `1px solid ${alpha(dark ? '#fff' : '#26A69A', dark ? 0.22 : 0.28)}`,
        boxShadow: dark
            ? `0 8px 36px ${alpha('#000', 0.26)}, inset 0 1px 0 ${alpha('#fff', 0.1)}`
            : `0 10px 32px ${alpha('#0d3d38', 0.18)}, inset 0 1px 0 ${alpha('#fff', 0.78)}`,
    };
}

/** Stronger hover elevation; optional accent tint in the outer shadow. */
function getHomePageGlassHoverShadowSx(theme: Theme, accentColor?: string) {
    const dark = theme.palette.mode === 'dark';
    const accentGlow = accentColor
        ? `, 0 14px 40px ${alpha(accentColor, dark ? 0.26 : 0.26)}`
        : '';
    return {
        borderColor: accentColor ? alpha(accentColor, dark ? 0.45 : 0.44) : alpha(dark ? '#fff' : '#26A69A', dark ? 0.3 : 0.36),
        boxShadow: dark
            ? `0 16px 44px ${alpha('#000', 0.32)}, inset 0 1px 0 ${alpha('#fff', 0.12)}${accentGlow}`
            : `0 18px 40px ${alpha('#0d3d38', 0.22)}, inset 0 1px 0 ${alpha('#fff', 0.85)}${accentGlow}`,
    };
}

/** Mobile services row: no backdrop blur — same glass stack inside `overflow-x: scroll` draws a bad solid rect in WebKit/Safari. */
function getHomePageMobileCarouselCardBaseSx(theme: Theme) {
    return {
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
    };
}

const paginationDotSx = (active: boolean) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: active ? '#ffffff' : 'rgba(255,255,255,0.4)',
    transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
    boxShadow: active ? '0 0 12px rgba(255,255,255,0.9), 0 0 24px rgba(255,255,255,0.5)' : 'none',
});

/** How Virtoly works (mobile glass card): light surface — dark dots; dark mode keeps high-contrast light dots. */
function howItWorksMobilePaginationDotSx(theme: Theme, active: boolean) {
    if (theme.palette.mode === 'dark') {
        return {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: active ? '#ffffff' : 'rgba(255,255,255,0.4)',
            transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
            boxShadow: active ? '0 0 12px rgba(255,255,255,0.9), 0 0 24px rgba(255,255,255,0.5)' : 'none',
        };
    }
    const accent = getButtonColor(theme);
    return {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: active ? accent : alpha(theme.palette.text.primary, 0.28),
        transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
        boxShadow: active ? `0 0 0 2px ${alpha(accent, 0.35)}, 0 2px 10px ${alpha(accent, 0.22)}` : 'none',
    };
}

/** Subtle border + radius around each step sprite (same idea as the old icon gradient wells). */
function howItWorksStepIconFrameSx(theme: Theme) {
    const dark = theme.palette.mode === 'dark';
    return {
        borderRadius: 2,
        border: `1px solid ${alpha(dark ? '#fff' : '#26A69A', dark ? 0.15 : 0.22)}`,
        boxSizing: 'border-box' as const,
    };
}

/**
 * HiDPI / glass-card stacking: own composited layer and high-quality downscaling so step
 * art stays sharp under backdrop blur.
 */
const howItWorksSpriteRenderQualitySx = {
    isolation: 'isolate' as const,
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    // Chromium: better downscale filter (not in all `SxProps` typings yet).
    imageRendering: 'high-quality',
} as SxProps<Theme>;

/** Horizontal scroll: overflow + layout. touch-action for descendants is in mobile.css so dragging on tiles scrolls (overrides [onClick] manipulation). */
const horizontalScrollContainerSx = {
    overflowX: 'scroll' as const,
    overflowY: 'hidden' as const,
    touchAction: 'pan-x pan-y',
    overscrollBehaviorX: 'contain' as const,
    width: '100%',
    minHeight: 0,
};

/** Subpixel / iOS Safari: last-page scrollLeft is often slightly below n * step. */
const SCROLL_END_INDEX_EPSILON_PX = 6;

function useScrollIndex(
    ref: React.RefObject<HTMLDivElement | null>,
    cardWidth: number,
    maxIndex: number,
    onScrollExtra?: () => void
): [number, () => void] {
    const [index, setIndex] = useState(0);
    const handleScroll = React.useCallback(() => {
        if (!ref.current) return;
        const el = ref.current;
        const maxScroll = el.scrollWidth - el.clientWidth;
        let newIndex: number;
        if (maxScroll <= 0) {
            newIndex = 0;
        } else if (el.scrollLeft >= maxScroll - SCROLL_END_INDEX_EPSILON_PX) {
            newIndex = maxIndex;
        } else {
            newIndex = Math.min(maxIndex, Math.max(0, Math.round(el.scrollLeft / cardWidth)));
        }
        setIndex(newIndex);
        onScrollExtra?.();
    }, [ref, cardWidth, maxIndex, onScrollExtra]);
    return [index, handleScroll];
}

function SectionHeader({
    overline,
    title,
    subtitle,
    isMobile,
    sx,
}: {
    overline?: string;
    title: string;
    subtitle?: string;
    isMobile: boolean;
    sx?: SxProps<Theme>;
}) {
    return (
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 }, ...sx }}>
            {overline && (
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: 2 }}>
                    {overline}
                </Typography>
            )}
            <Typography
                variant={isMobile ? 'h4' : 'h3'}
                component="h2"
                fontWeight={700}
                sx={{ color: '#ffffff', mt: overline ? 1 : 0, fontSize: responsiveFontSize.h2 }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 2, maxWidth: 500, mx: 'auto' }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

const advantageItems = [
    { icon: SecurityIcon, title: 'Payment Protection', desc: 'Your payment is held securely until the job is done right.', descShort: 'Your payment is held securely until the job is done right.' },
    { icon: VerifiedUserIcon, title: 'Highly Rated Experts', desc: 'Verified reviews, badges, and ratings. Know who you are hiring.', descShort: 'Verified reviews, badges, and ratings. Know who you are hiring.' },
    { icon: CalendarTodayIcon, title: 'Flexible Booking', desc: 'Rescheduling and direct assign to your preferred expert.', descShort: 'Rescheduling and direct assign to your preferred expert.' },
    { icon: SecurityIcon, title: 'Virtoly Protection', desc: 'Our dispute resolution team steps in to ensure a fair outcome for both sides.', descShort: 'Our dispute resolution team steps in to ensure a fair outcome for both sides.' },
];

export type HomePageProps = {
    /** Deep links (e.g. /help/how-virtoly-works-customer): open “How does it work?” on load. */
    howItWorksAutoOpen?: boolean;
    /** 0 = Customer tab, 1 = Expert tab — see HowItWorksModal. */
    howItWorksModalInitialTab?: 0 | 1;
};

export default function HomePage({
    howItWorksAutoOpen = false,
    howItWorksModalInitialTab = 0,
}: HomePageProps = {}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const showHeroPhone = useMediaQuery(theme.breakpoints.up(900));
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const allExpertsOption = React.useMemo<HeroSearchServiceOption>(
        () => ({
            name: HERO_ALL_SERVICES_OPTION_LABEL,
            icon: <Groups2Icon sx={{ fontSize: '1.35rem' }} />,
            allExperts: true,
        }),
        [],
    );
    const { heroSearchOptions, loading: servicesLoading, onHeroSearchOpen } = useHeroSearchServices(allExpertsOption);

    React.useEffect(() => {
        let cancelled = false;
        // Keep the boot spinner until HomePage has painted (desktop + native).
        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!cancelled) dismissHomePageBootLoader();
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
        };
    }, []);

    const [selectedCategory, setSelectedCategory] = React.useState<HeroSearchServiceOption | undefined>(undefined);
    const [locationDialogOpen, setLocationDialogOpen] = React.useState(false);
    const [authModalOpen, setAuthModalOpen] = React.useState(false);
    const [workerAuthModalOpen, setWorkerAuthModalOpen] = React.useState(false);
    const [getStartedOpen, setGetStartedOpen] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState({
        open: false,
        message: '',
        severity: 'info' as 'info' | 'error'
    });
    const { city, province, isInitialized } = useLocation();
    const routerLocation = useRouterLocation();
    const navigate = useNavigate();
    const auth = useAuth();
    const [howItWorksOpen, setHowItWorksOpen] = useState(howItWorksAutoOpen);
    const [deferredModalsReady, setDeferredModalsReady] = useState(howItWorksAutoOpen);

    useEffect(() => {
        scheduleDeferred(() => setDeferredModalsReady(true));
    }, []);

    const openLocationDialog = React.useCallback(() => {
        setDeferredModalsReady(true);
        setLocationDialogOpen(true);
    }, []);

    const openWorkerAuthModal = React.useCallback(() => {
        setDeferredModalsReady(true);
        setWorkerAuthModalOpen(true);
    }, []);

    const openCustomerAuthModal = React.useCallback(() => {
        setDeferredModalsReady(true);
        setAuthModalOpen(true);
    }, []);

    const getStartedAutoPromptedRef = React.useRef(false);
    const pendingGetStartedAfterCityRef = React.useRef(false);
    const cityAutoPromptedRef = React.useRef(false);

    const canAutoPromptGetStarted = React.useCallback(() => {
        if (auth.isLogin() || howItWorksAutoOpen) {
            return false;
        }
        if (getStartedAutoPromptedRef.current) {
            return false;
        }
        try {
            if (sessionStorage.getItem('virtoly_get_started_seen') === '1') {
                return false;
            }
        } catch {
            // Continue and show the chooser if storage is unavailable
        }
        return true;
    }, [auth.isLogin, howItWorksAutoOpen]);

    const openGetStartedChooser = React.useCallback(() => {
        setDeferredModalsReady(true);
        setGetStartedOpen(true);
    }, []);

    const tryAutoOpenGetStarted = React.useCallback(() => {
        if (!canAutoPromptGetStarted()) {
            return;
        }
        getStartedAutoPromptedRef.current = true;
        openGetStartedChooser();
    }, [canAutoPromptGetStarted, openGetStartedChooser]);

    const handleLocationDialogClose = React.useCallback(() => {
        setLocationDialogOpen(false);
        if (pendingGetStartedAfterCityRef.current) {
            pendingGetStartedAfterCityRef.current = false;
            tryAutoOpenGetStarted();
        }
    }, [tryAutoOpenGetStarted]);

    const openHowItWorksModal = React.useCallback(() => {
        setDeferredModalsReady(true);
        setHowItWorksOpen(true);
    }, []);

    const servicesRef = React.useRef<HTMLDivElement>(null);
    const [servicesGlowActive, setServicesGlowActive] = useState(false);
    const [howItWorksStepIndex, setHowItWorksStepIndex] = useState(0);
    const [howItWorksAnimDir, setHowItWorksAnimDir] = useState<1 | -1>(1);
    const howItWorksTouchStartRef = React.useRef<{ x: number; y: number; id: number } | null>(null);

    const HOW_IT_WORKS_SWIPE_MIN_PX = 48;
    const handleHowItWorksTouchStart = React.useCallback((e: React.TouchEvent) => {
        const t = e.touches[0];
        if (!t) return;
        howItWorksTouchStartRef.current = { x: t.clientX, y: t.clientY, id: t.identifier };
    }, []);
    const handleHowItWorksTouchEnd = React.useCallback((e: React.TouchEvent) => {
        const start = howItWorksTouchStartRef.current;
        howItWorksTouchStartRef.current = null;
        if (!start) return;
        const endTouch = Array.from(e.changedTouches).find((ct) => ct.identifier === start.id);
        if (!endTouch) return;
        const dx = endTouch.clientX - start.x;
        const dy = endTouch.clientY - start.y;
        if (Math.abs(dx) < HOW_IT_WORKS_SWIPE_MIN_PX) return;
        if (Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) {
            setHowItWorksAnimDir(1);
            setHowItWorksStepIndex((i) => (i + 1) % howItWorksSteps.length);
        } else {
            setHowItWorksAnimDir(-1);
            setHowItWorksStepIndex((i) => (i - 1 + howItWorksSteps.length) % howItWorksSteps.length);
        }
    }, []);

    // Reset the 5s auto-advance whenever the step changes so a swipe is not followed immediately by a timer tick.
    useEffect(() => {
        if (isMdUp || prefersReducedMotion) return;
        const id = window.setInterval(() => {
            setHowItWorksAnimDir(1);
            setHowItWorksStepIndex((i) => (i + 1) % howItWorksSteps.length);
        }, 5000);
        return () => window.clearInterval(id);
    }, [isMdUp, prefersReducedMotion, howItWorksStepIndex]);
    const mobileServiceCarouselLastIndex = serviceCategories.length;
    const [servicesScrollIndex, handleServicesScroll] = useScrollIndex(
        servicesRef,
        156,
        mobileServiceCarouselLastIndex,
        () => setServicesGlowActive(true)
    );

    React.useEffect(() => {
        if (!isInitialized || city || province) {
            return;
        }
        if (cityAutoPromptedRef.current) {
            return;
        }
        if (authModalOpen || workerAuthModalOpen) {
            return;
        }
        cityAutoPromptedRef.current = true;
        if (canAutoPromptGetStarted()) {
            pendingGetStartedAfterCityRef.current = true;
        }
        openLocationDialog();
    }, [city, province, isInitialized, authModalOpen, workerAuthModalOpen, openLocationDialog, canAutoPromptGetStarted]);

    React.useEffect(() => {
        if (!isInitialized || (!city && !province)) {
            return;
        }
        tryAutoOpenGetStarted();
    }, [isInitialized, city, province, tryAutoOpenGetStarted]);

    React.useEffect(() => {
        const error = (routerLocation.state as { notificationError?: string } | null)?.notificationError;
        if (error === 'different_account') {
            setSnackbar({
                open: true,
                message: 'This notification belongs to a different account. Please log in with the correct account to view project details.',
                severity: 'error'
            });
            navigate('/', { replace: true });
        }
    }, [routerLocation.state, navigate]);

    React.useEffect(() => {
        if (!isMdUp) {
            return;
        }
        const preloadImage = (src: string) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            document.head.appendChild(link);
        };
        preloadImage(iPhoneLogoDark);
        preloadImage(iPhoneLogoLight);
    }, [isMdUp]);

    const handleSearch = () => {
        if (!city) {
            openLocationDialog();
            return;
        }
        if (!selectedCategory || selectedCategory.allExperts) {
            navigate('/find-experts-in-city');
            return;
        }
        const databaseCategory = displayNameToDatabaseCategory(selectedCategory.name);
        navigate(`/find-experts-in-city?category=${encodeURIComponent(databaseCategory)}`);
    };

    const handleCustomerCta = React.useCallback(() => {
        navigate('/customer-create-order');
    }, [navigate]);

    const handleWorkerCta = React.useCallback(() => {
        if (auth.isLogin() && auth.credentialType === 2) navigate('/available-projects');
        else openWorkerAuthModal();
    }, [auth, navigate, openWorkerAuthModal]);

    const handleGetStartedSelect = React.useCallback((type: 'customer' | 'worker') => {
        setGetStartedOpen(false);
        if (type === 'customer') openCustomerAuthModal();
        else openWorkerAuthModal();
    }, [openCustomerAuthModal, openWorkerAuthModal]);

    React.useEffect(() => {
        const handleWindowScroll = () => setServicesGlowActive(false);
        window.addEventListener('scroll', handleWindowScroll, true);
        return () => window.removeEventListener('scroll', handleWindowScroll, true);
    }, []);

    const handleOpenIOSStore = () => {
        window.open(APP_STORE_URL, '_blank');
    };

    const handleOpenAndroidStore = () => {
        window.open(PLAY_STORE_URL, '_blank');
    };

    const howItWorksActiveStep = howItWorksSteps[howItWorksStepIndex];

    return (
        <Box sx={{ 
            flexGrow: 1, 
            minHeight: '100vh',
            position: 'relative',
            zIndex: Z_INDEX.BACKGROUND,
            width: '100%',
            background: `linear-gradient(180deg, #5ECDBF 0%, hsl(222, 52%, 40%) 100%)`,
            [theme.breakpoints.up('lg')]: {
                backgroundAttachment: 'fixed',
                '&::before': { display: 'none' },
            },
            [theme.breakpoints.down('lg')]: {
                backgroundAttachment: 'scroll',
                '&::before': {
                    content: '""',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: `linear-gradient(180deg, #5ECDBF 0%, hsl(222, 52%, 40%) 100%)`,
                    zIndex: -1,
                    pointerEvents: 'none',
                },
            },
        }}>
            {deferredModalsReady && (
                <Suspense fallback={null}>
                    <CitySelectionModal
                        open={locationDialogOpen}
                        onClose={handleLocationDialogClose}
                    />
                </Suspense>
            )}
            <CustomSnackbar
                open={snackbar.open}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                severity={snackbar.severity}
            />

            {/* Hero Section */}
            <Box sx={{
                color: '#ffffff',
                pt: { xs: 4, sm: 6, md: 8, lg: 10 },
                pb: { xs: 6, sm: 8, md: 10 },
                position: 'relative',
                overflow: 'visible'
            }}>
                <Container maxWidth="lg" sx={{ px: CONTAINER_PX, position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} sx={(theme) => ({ minWidth: 0, [theme.breakpoints.up(900)]: { flexBasis: '58.333333%', maxWidth: '58.333333%' } })}>
                            <Box className="hero-animate-in">
                                <Typography 
                                    variant={isMobile ? "h3" : isTablet ? "h2" : "h1"} 
                                    component="h1" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        mb: 3,
                                        color: '#ffffff',
                                        fontSize: {
                                            xs: responsiveFontSize.heroTitle.xs,
                                            sm: responsiveFontSize.heroTitle.sm,
                                            md: '2.875rem',
                                            lg: responsiveFontSize.heroTitle.lg,
                                        },
                                        lineHeight: 1.2,
                                        overflow: 'visible',
                                    }}
                                >
                                    Book home services
                                    <br />
                                    <Box
                                        component="span"
                                        sx={{
                                            background: 'linear-gradient(135deg, #FFD54F 0%, #FFB74D 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                        }}
                                    >
                                        with trusted experts
                                        <br />
                                        in Canada
                                    </Box>
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mb: 4,
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: responsiveFontSize.headingMdBreakpoints,
                                        fontWeight: 400,
                                        maxWidth: 600,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    Compare quotes, schedule online, and manage every project in one place. Skilled professional? Find your next job on Virtoly.
                                </Typography>
                            </Box>

                            {/* Search Card */}
                            <Box className="hero-animate-in hero-animate-in-delayed">
                                <ContentCard
                                    sx={{
                                        ...getHomePageGlassTileSx(theme),
                                        background:
                                            theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.background.paper, 0.88)
                                                : alpha('#fff', 0.97),
                                        borderRadius: 3,
                                        p: { xs: 2, sm: 3 },
                                        mb: 4,
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: theme.palette.text.primary }}>
                                        What service do you need?
                                    </Typography>
                                    <Box sx={(theme) => ({
                                        display: 'flex',
                                        gap: 2,
                                        flexDirection: 'column',
                                        [theme.breakpoints.up(1100)]: { flexDirection: 'row' },
                                    })}
                                    >
                                        <Autocomplete<HeroSearchServiceOption, false, true, false>
                                            fullWidth
                                            disableClearable
                                            options={heroSearchOptions}
                                            onOpen={onHeroSearchOpen}
                                            getOptionLabel={(option) => option.name}
                                            isOptionEqualToValue={(a, b) =>
                                                b != null &&
                                                a.name === b.name &&
                                                Boolean(a.allExperts) === Boolean(b.allExperts)
                                            }
                                            loading={servicesLoading}
                                            value={selectedCategory}
                                            onChange={(_event, newValue) => setSelectedCategory(newValue)}
                                            slotProps={{
                                                popper: { sx: { zIndex: Z_INDEX.DIALOG } },
                                                listbox: { sx: { overflowY: 'scroll' } },
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder={HERO_SERVICE_FIELD_PLACEHOLDER}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        readOnly: true,
                                                    }}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon sx={{ color: getIconColor(theme) }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        background: theme.palette.background.paper,
                                                        borderRadius: 2,
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        }
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{ color: getIconColor(theme) }}>{option.icon}</Box>
                                                    <Typography>{option.name}</Typography>
                                                </Box>
                                            )}
                                        />
                                        <TextField
                                            variant="outlined"
                                            placeholder="Location"
                                            value={city || "Select city"}
                                            onClick={openLocationDialog}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LocationOnIcon sx={{ color: getIconColor(theme) }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                minWidth: { sm: 200 },
                                                background: theme.palette.background.paper,
                                                borderRadius: 2,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <CustomButton
                                            variant="contained"
                                            size="large"
                                            onClick={handleSearch}
                                            sx={{
                                                px: 4,
                                                height: 56,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                minWidth: { sm: 120 },
                                            }}
                                        >
                                            Search
                                        </CustomButton>
                                    </Box>
                                </ContentCard>
                            </Box>

                        </Grid>

                        {/* iPhone Mockup – grid slot always present for layout; content mounts from 900px up only. */}
                        <Grid item xs={12} sx={(theme) => ({ display: 'none', justifyContent: 'center', [theme.breakpoints.up(900)]: { display: 'flex', flexBasis: '41.666667%', maxWidth: '41.666667%' } })}>
                        {showHeroPhone && (
                            <Box className="hero-phone-animate-in">
                                <Paper
                                    elevation={0}
                                    sx={{
                                        width: 300,
                                        height: 620,
                                        position: 'relative',
                                        borderRadius: 7,
                                        p: 1.25,
                                        overflow: 'visible',
                                        background: 'linear-gradient(165deg, #3d3d40 0%, #252528 38%, #121214 100%)',
                                        border: `1px solid ${alpha('#fff', 0.1)}`,
                                        boxShadow: `
                                            0 36px 72px -20px ${alpha('#000', 0.45)},
                                            0 16px 32px -12px ${alpha('#000', 0.28)},
                                            inset 0 1px 0 ${alpha('#fff', 0.1)}
                                        `,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: '100%',
                                            borderRadius: 5,
                                            overflow: 'hidden',
                                            position: 'relative',
                                            border: `1px solid ${alpha('#fff', 0.14)}`,
                                            boxShadow: `inset 0 0 0 1px ${alpha('#000', 0.35)}`,
                                            // Match studio backdrop in wallpaper assets (gaps from translateY, edges under cover).
                                            bgcolor: theme.palette.mode === 'dark' ? '#92928f' : '#ffffff',
                                        }}
                                    >
                                        {/* Wallpaper */}
                                        <Box
                                            component="img"
                                            src={theme.palette.mode === 'dark' ? iPhoneLogoDark : iPhoneLogoLight}
                                            alt=""
                                            aria-hidden
                                            width={552}
                                            height={1144}
                                            loading="lazy"
                                            decoding="async"
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                zIndex: Z_INDEX.BASE,
                                                objectFit: 'cover',
                                                objectPosition: 'center top',
                                                display: 'block',
                                                transform: 'translateY(15px)',
                                            }}
                                        />
                                        {/* Dynamic Island (masks legacy notch in asset; reads as current-gen hardware) */}
                                        <Box
                                            aria-hidden
                                            sx={{
                                                position: 'absolute',
                                                top: 13,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: 92,
                                                height: 27,
                                                borderRadius: 999,
                                                zIndex: Z_INDEX.BASE_LAYER_2,
                                                bgcolor: '#0a0a0a',
                                                boxShadow: `
                                                    inset 0 1px 0 ${alpha('#fff', 0.12)},
                                                    inset 0 -2px 6px ${alpha('#000', 0.5)},
                                                    0 4px 14px ${alpha('#000', 0.45)}
                                                `,
                                            }}
                                        />
                                        {/* Status Bar */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            px: { md: 1.5, lg: 2 },
                                            py: { md: 0.75, lg: 1 },
                                            zIndex: Z_INDEX.BASE_LAYER_5,
                                        }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: responsiveFontSize.captionMdLg,
                                                    color: '#000000',
                                                }}
                                            >
                                                9:31
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                <Box sx={{ width: { md: 14, lg: 16 }, height: { md: 8, lg: 10 }, borderRadius: 1, background: '#000000' }} />
                                                <Box sx={{ width: { md: 3, lg: 4 }, height: { md: 3, lg: 4 }, borderRadius: '50%', background: '#000000' }} />
                                            </Box>
                                        </Box>
                                        {/* Draggable Bottom Sheet */}
                                        <DraggableBottomSheet
                                            minHeight={20}
                                            maxHeight={100}
                                            initialHeight={50}
                                            middleHeight={50}
                                            paperSx={{
                                                background: alpha(
                                                    theme.palette.background.paper,
                                                    theme.palette.mode === 'dark' ? 0.72 : 0.86,
                                                ),
                                                backdropFilter: 'blur(18px) saturate(1.12)',
                                                WebkitBackdropFilter: 'blur(18px) saturate(1.12)',
                                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                                                boxShadow: `0 -18px 48px ${alpha('#000', theme.palette.mode === 'dark' ? 0.35 : 0.1)}`,
                                            }}
                                        >
                                            <Box sx={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        mb: 2,
                                                        color: theme.palette.text.primary,
                                                        fontSize: responsiveFontSize.titleMdLg,
                                                    }}
                                                >
                                                    Book a Service
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mb: 3,
                                                        color: theme.palette.text.secondary,
                                                        fontSize: responsiveFontSize.bodyMdLg,
                                                    }}
                                                >
                                                    Find trusted experts<br />for your needs
                                                </Typography>
                                                <CustomButton
                                                    variant="contained"
                                                    size="medium"
                                                    onClick={handleCustomerCta}
                                                    sx={{
                                                        px: { xs: 4, sm: 4 },
                                                        py: { xs: 2, sm: 1.5 },
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontSize: responsiveFontSize.bodyMdDip,
                                                        minHeight: { xs: '48px', sm: 'auto' },
                                                        minWidth: 'auto',
                                                    }}
                                                >
                                                    Book Now
                                                </CustomButton>
                                            </Box>
                                        </DraggableBottomSheet>
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* How It Works */}
            <Box sx={{ pt: { xs: 0.5, sm: 1, md: 1.5 }, pb: SECTION_PY, position: 'relative' }}>
                <Container maxWidth="lg" sx={{ px: CONTAINER_PX }}>
                    <Box>
                        <SectionHeader
                            title="How to book local home services on Virtoly"
                            isMobile={isMobile}
                            sx={{ mb: { xs: 2, sm: 2.5 } }}
                        />

                        {/* How It Works Steps – below md: single tile, swipe, dots, auto-advance every 5s */}
                        <Box
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                maxWidth: { xs: 'min(100%, 560px)', sm: 600 },
                                mx: 'auto',
                                px: { xs: 0, sm: 0.5 },
                            }}
                            onTouchStart={handleHowItWorksTouchStart}
                            onTouchEnd={handleHowItWorksTouchEnd}
                        >
                            <ContentCard
                                sx={{
                                    ...getHomePageGlassTileSx(theme),
                                    p: { xs: 2.75, sm: 3.25 },
                                    borderRadius: 3,
                                    textAlign: 'center',
                                    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                                    touchAction: 'pan-x pan-y',
                                    '@media (hover: hover)': {
                                        '&:hover': getHomePageGlassHoverShadowSx(theme),
                                    },
                                }}
                            >
                                <Box sx={{ overflow: 'hidden', position: 'relative' }} aria-live="polite" aria-atomic="true">
                                    <Box
                                        key={howItWorksActiveStep.step}
                                        className={prefersReducedMotion ? undefined : 'how-it-works-step-animate'}
                                        data-dir={howItWorksAnimDir}
                                        sx={{ width: '100%' }}
                                    >
                                            <Typography variant="overline" sx={{ display: 'inline-block', color: '#26A69A', fontWeight: 700, letterSpacing: 1.5, mb: 1.25, fontSize: fontSizeFixed.rem07 }}>
                                                Step {howItWorksActiveStep.step}
                                            </Typography>
                                            <Box
                                                sx={
                                                    {
                                                        ...howItWorksStepIconFrameSx(theme),
                                                        ...howItWorksSpriteRenderQualitySx,
                                                        position: 'relative',
                                                        width: { xs: '88px', sm: '96px' },
                                                        height: { xs: '88px', sm: '96px' },
                                                        mx: 'auto',
                                                        mb: 1.5,
                                                        overflow: 'hidden',
                                                    } as SxProps<Theme>
                                                }
                                            >
                                                <Box
                                                    component="img"
                                                    src={HOW_IT_WORKS_STEP_IMAGES[howItWorksStepIndex]}
                                                    alt={HOW_IT_WORKS_STEP_ALTS[howItWorksStepIndex]}
                                                    width={192}
                                                    height={192}
                                                    loading="lazy"
                                                    decoding="async"
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                                sx={{
                                                    color: 'text.primary',
                                                    mb: 0,
                                                    fontSize: fontSizeFixed.rem1125,
                                                    lineHeight: 1.3,
                                                    textAlign: 'center',
                                                    minHeight: '2.6em',
                                                }}
                                            >
                                                {howItWorksActiveStep.title} {howItWorksActiveStep.subtitle}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    lineHeight: 1.6,
                                                    fontSize: fontSizeFixed.rem0875,
                                                    textAlign: 'center',
                                                    minHeight: '3.2em',
                                                }}
                                            >
                                                {howItWorksActiveStep.desc}
                                            </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.25, mt: 2 }}>
                                    {howItWorksSteps.map((_, dot) => (
                                        <Box
                                            key={dot}
                                            component="button"
                                            type="button"
                                            aria-label={`Show step ${dot + 1}`}
                                            aria-current={howItWorksStepIndex === dot ? 'true' : undefined}
                                            onClick={() => {
                                                if (dot === howItWorksStepIndex) return;
                                                setHowItWorksAnimDir(shortestHowItWorksDirection(howItWorksStepIndex, dot, howItWorksSteps.length));
                                                setHowItWorksStepIndex(dot);
                                            }}
                                            sx={{
                                                p: 0.75,
                                                m: -0.75,
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                borderRadius: '50%',
                                                lineHeight: 0,
                                                '&:focus-visible': {
                                                    outline: `2px solid ${getButtonColor(theme)}`,
                                                    outlineOffset: 2,
                                                },
                                            }}
                                        >
                                            <Box sx={howItWorksMobilePaginationDotSx(theme, howItWorksStepIndex === dot)} />
                                        </Box>
                                    ))}
                                </Box>
                            </ContentCard>
                        </Box>

                        {/* How It Works Steps – desktop: 4-column grid (900px+) */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Grid container spacing={4} justifyContent="center" sx={{ width: '100%' }}>
                                {howItWorksSteps.map((item, index) => (
                                    <Grid item xs={12} md={3} key={index}>
                                        <ContentCard sx={{ ...getHomePageGlassTileSx(theme), height: '100%', p: 3, borderRadius: 3, textAlign: 'center', transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease', '&:hover': { transform: 'translateY(-4px)', ...getHomePageGlassHoverShadowSx(theme) } }}>
                                            <Typography variant="overline" sx={{ display: 'inline-block', color: '#26A69A', fontWeight: 700, letterSpacing: 1.5, mb: 1.5, fontSize: fontSizeFixed.rem07 }}>Step {item.step}</Typography>
                                            <Box
                                                sx={
                                                    {
                                                        ...howItWorksStepIconFrameSx(theme),
                                                        ...howItWorksSpriteRenderQualitySx,
                                                        position: 'relative',
                                                        width: '96px',
                                                        height: '96px',
                                                        mx: 'auto',
                                                        mb: 2,
                                                        overflow: 'hidden',
                                                    } as SxProps<Theme>
                                                }
                                            >
                                                <Box
                                                    component="img"
                                                    src={HOW_IT_WORKS_STEP_IMAGES[index]}
                                                    alt={HOW_IT_WORKS_STEP_ALTS[index]}
                                                    width={192}
                                                    height={192}
                                                    loading="lazy"
                                                    decoding="async"
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5, fontSize: fontSizeFixed.rem1125 }}>{item.title} {item.subtitle}</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: fontSizeFixed.rem0875 }}>{item.desc}</Typography>
                                        </ContentCard>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Expert signup — narrow landscape strip */}
            <Box sx={{ pb: { xs: 2, sm: 3 }, pt: 0 }}>
                <Container maxWidth="md" sx={{ px: CONTAINER_PX }}>
                    <ContentCard
                        sx={{
                            ...getHomePageGlassTileSx(theme),
                            p: { xs: 2, sm: 2.5, md: 3 },
                            borderRadius: 3,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'nowrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: { xs: 1.25, sm: 3 },
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    flexShrink: 0,
                                    width: { xs: '52%', sm: '42%' },
                                    minWidth: { xs: 128, sm: 120 },
                                    maxWidth: { xs: 280, sm: 320 },
                                    height: { xs: 136, sm: 150, md: 160 },
                                    alignSelf: 'center',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    background:
                                        theme.palette.mode === 'dark'
                                            ? `linear-gradient(145deg, ${alpha('#fff', 0.06)} 0%, ${alpha('#26A69A', 0.12)} 100%)`
                                            : `linear-gradient(145deg, ${alpha('#fff', 0.72)} 0%, ${alpha('#26A69A', 0.14)} 100%)`,
                                    border: `1px solid ${alpha(theme.palette.mode === 'dark' ? '#fff' : '#26A69A', theme.palette.mode === 'dark' ? 0.12 : 0.24)}`,
                                    boxShadow: `inset 0 0 24px ${alpha('#fff', theme.palette.mode === 'dark' ? 0.04 : 0.48)}`,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={expertSignup}
                                    alt="Join Virtoly as a local service expert — sign up to receive job requests"
                                    width={640}
                                    height={337}
                                    loading="lazy"
                                    decoding="async"
                                    sx={{
                                        position: 'absolute',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                        ...(isTablet
                                            ? {
                                                top: 0,
                                                left: 0,
                                                width: { xs: 'calc(100% + 8px)', sm: 'calc(100% + 6px)' },
                                                objectPosition: { xs: '48% top', sm: '49% top' },
                                                transform: { xs: 'translateX(-2px)', sm: 'translateX(-1px)' },
                                            }
                                            : {
                                                inset: 0,
                                                width: '100%',
                                                objectPosition: { xs: '50% top', sm: 'center top' },
                                            }),
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center',
                                    gap: { xs: 0.75, sm: 1 },
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    fontWeight={800}
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: {
                                            xs: fontSizeFixed.rem1,
                                            sm: responsiveFontSize.h3.xs,
                                            md: responsiveFontSize.h3.sm,
                                            lg: responsiveFontSize.h3.md,
                                        },
                                        lineHeight: 1.2,
                                        mb: 0.25,
                                    }}
                                >
                                    Join as an Expert today, it's free!
                                </Typography>
                                <CustomButton
                                    variant="contained"
                                    size="medium"
                                    onClick={handleWorkerCta}
                                    sx={{
                                        mt: { xs: 0.5, sm: 1, md: 1.25 },
                                        px: { xs: 2, sm: 3, md: 4, lg: 4.5 },
                                        py: { xs: 0.75, sm: 1, md: 1.15, lg: 1.25 },
                                        fontSize: {
                                            xs: fontSizeFixed.rem08125,
                                            sm: responsiveFontSize.buttonMedium.sm,
                                            md: responsiveFontSize.buttonMedium.md,
                                            lg: responsiveFontSize.buttonLarge.md,
                                        },
                                    }}
                                >
                                    Sign up
                                </CustomButton>
                            </Box>
                        </Box>
                    </ContentCard>
                </Container>
            </Box>

            {/* Service Categories Section */}
            <Container maxWidth="lg" sx={{ py: SECTION_PY, px: CONTAINER_PX }}>
                <Box>
                    <SectionHeader title="Browse local home service categories" isMobile={isMobile} />

                    {/* Desktop: Grid Layout with Glow Effect (no stacking – grid from md up) */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            {serviceCategories.map((service, index) => {
                                const IconComponent = service.icon;
                                return (
                                    <Grid item key={index} xs={6} sm={4} md={3}>
                                        <Box sx={{ height: '100%' }}>
                                            <Box
                                                onClick={() => {
                                                    const servicePath = categoryKeyToPath(service.category);
                                                    navigate(`/customer-service/${servicePath}?subcategory=${encodeURIComponent(service.subcategory)}`);
                                                }}
                                                sx={{ cursor: 'pointer', height: '100%' }}
                                            >
                                                <ContentCard
                                                    sx={{
                                                        ...getHomePageGlassTileSx(theme),
                                                        textAlign: 'center',
                                                        p: { xs: 2, sm: 2.5, md: 2, lg: 2 },
                                                        borderRadius: 3,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        height: '100%',
                                                        minHeight: { xs: 240, sm: 240, md: 200, lg: 180 },
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        '&:hover': {
                                                            transform: 'translateY(-8px) scale(1.02)',
                                                            ...getHomePageGlassHoverShadowSx(theme, service.color),
                                                            '& .service-icon-wrapper': {
                                                                boxShadow: `0 0 30px ${alpha(service.color, 0.6)}, 0 0 60px ${alpha(service.color, 0.3)}`,
                                                                transform: 'scale(1.1)',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Box className="service-icon-wrapper" sx={{
                                                        width: { xs: 64, sm: 64, md: 48, lg: 44 },
                                                        height: { xs: 64, sm: 64, md: 48, lg: 44 },
                                                        minWidth: { xs: 64, sm: 64, md: 48, lg: 44 },
                                                        minHeight: { xs: 64, sm: 64, md: 48, lg: 44 },
                                                        flexShrink: 0,
                                                        borderRadius: 3,
                                                        background: alpha(service.color, 0.15),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mx: 'auto',
                                                        mb: { xs: 2.5, sm: 2.5, md: 2.25, lg: 2 },
                                                        transition: 'all 0.3s ease',
                                                    }}>
                                                        <IconComponent sx={{ fontSize: responsiveIconSize.serviceCardIcon, color: service.color }} />
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={700}
                                                        sx={{
                                                            mb: { xs: 0.5, md: 0.25, lg: 0.25 },
                                                            fontSize: responsiveFontSize.serviceTitle,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            minHeight: { xs: 36, md: 34, lg: 32 },
                                                        }}
                                                    >
                                                        {service.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: 1.4,
                                                            minHeight: { xs: 40, md: 36, lg: 34 },
                                                            fontSize: responsiveFontSize.serviceDesc,
                                                        }}
                                                    >
                                                        {service.description}
                                                    </Typography>
                                                </ContentCard>
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>

                    {/* Mobile: horizontal strip — tiles are direct flex children so x mandatory snap has one target per card (Safari was snapping back with a single inner wrapper). */}
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        <Box
                            ref={servicesRef}
                            onScroll={handleServicesScroll}
                            className="horizontal-scroll-container"
                            sx={{
                                ...horizontalScrollContainerSx,
                                pb: 2,
                                px: 1,
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'nowrap',
                                gap: 2,
                                alignContent: 'flex-start',
                                scrollSnapType: 'x mandatory',
                                scrollPaddingInline: (t) => t.spacing(1),
                                '& > *': { scrollSnapAlign: 'start', flexShrink: 0, touchAction: 'pan-x pan-y' },
                            }}
                        >
                            {serviceCategories.map((service, index) => {
                                const IconComponent = service.icon;
                                return (
                                    <Box
                                        key={index}
                                        onClick={() => {
                                            const servicePath = categoryKeyToPath(service.category);
                                            navigate(`/customer-service/${servicePath}?subcategory=${encodeURIComponent(service.subcategory)}`);
                                        }}
                                        sx={{ cursor: 'pointer', flexShrink: 0 }}
                                    >
                                        <ContentCard
                                            sx={{
                                                ...getHomePageMobileCarouselCardBaseSx(theme),
                                                textAlign: 'center',
                                                p: 2,
                                                borderRadius: 3,
                                                cursor: 'pointer',
                                                width: 140,
                                                minWidth: 140,
                                                minHeight: 220,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                transition: 'all 0.3s ease',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '@media (hover: hover)': {
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: `0 12px 30px ${alpha(service.color, 0.35)}`,
                                                        '& .mobile-icon-wrapper': {
                                                            boxShadow: `0 0 40px ${alpha(service.color, 0.8)}, 0 0 80px ${alpha(service.color, 0.5)}`,
                                                            transform: 'scale(1.12)',
                                                        },
                                                    },
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.98)',
                                                },
                                            }}
                                        >
                                            <Box className="mobile-icon-wrapper" sx={{
                                                width: 64,
                                                height: 64,
                                                minWidth: 64,
                                                minHeight: 64,
                                                flexShrink: 0,
                                                borderRadius: 3,
                                                background: alpha(service.color, 0.2),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 2,
                                                transition: 'all 0.3s ease',
                                                ...(servicesGlowActive && servicesScrollIndex === index && {
                                                    boxShadow: `0 0 24px ${alpha(service.color, 0.8)}, 0 0 48px ${alpha(service.color, 0.5)}`,
                                                    transform: 'scale(1.08)',
                                                }),
                                            }}>
                                                <IconComponent sx={{ fontSize: iconSizeFixed.px32, color: service.color }} />
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                                sx={{
                                                    fontSize: fontSizeFixed.rem1,
                                                    textAlign: 'center',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    minHeight: 44,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {service.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: fontSizeFixed.rem0875,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    lineHeight: 1.35,
                                                    minHeight: 36,
                                                    mt: 0.5,
                                                }}
                                            >
                                                {service.description}
                                            </Typography>
                                        </ContentCard>
                                    </Box>
                                );
                            })}
                            <Box
                                key="all-services"
                                onClick={handleCustomerCta}
                                sx={{ cursor: 'pointer', flexShrink: 0 }}
                            >
                                <ContentCard
                                    sx={{
                                        ...getHomePageMobileCarouselCardBaseSx(theme),
                                        textAlign: 'center',
                                        p: 2,
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        width: 140,
                                        minWidth: 140,
                                        minHeight: 220,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: `0 12px 30px ${alpha(ALL_SERVICES_ACCENT, 0.35)}`,
                                                '& .mobile-icon-wrapper': {
                                                    boxShadow: `0 0 40px ${alpha(ALL_SERVICES_ACCENT, 0.85)}, 0 0 80px ${alpha(ALL_SERVICES_ACCENT, 0.45)}`,
                                                    transform: 'scale(1.12)',
                                                },
                                            },
                                        },
                                        '&:active': {
                                            transform: 'scale(0.98)',
                                        },
                                    }}
                                >
                                    <Box
                                        className="mobile-icon-wrapper"
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            minWidth: 64,
                                            minHeight: 64,
                                            flexShrink: 0,
                                            borderRadius: 3,
                                            background: alpha(ALL_SERVICES_ACCENT, 0.22),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2,
                                            transition: 'all 0.3s ease',
                                            ...(servicesGlowActive && servicesScrollIndex === mobileServiceCarouselLastIndex && {
                                                boxShadow: `0 0 24px ${alpha(ALL_SERVICES_ACCENT, 0.85)}, 0 0 48px ${alpha(ALL_SERVICES_ACCENT, 0.45)}`,
                                                transform: 'scale(1.08)',
                                            }),
                                        }}
                                    >
                                        <Groups2Icon sx={{ fontSize: iconSizeFixed.px32, color: ALL_SERVICES_ACCENT }} />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        sx={{
                                            fontSize: fontSizeFixed.rem1,
                                            textAlign: 'center',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            minHeight: 44,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        All Services
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: fontSizeFixed.rem0875,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            lineHeight: 1.35,
                                            minHeight: 36,
                                            mt: 0.5,
                                        }}
                                    >
                                        Create an order for any service
                                    </Typography>
                                </ContentCard>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {Array.from({ length: mobileServiceCarouselLastIndex + 1 }, (_, dot) => (
                                <Box key={dot} sx={paginationDotSx(servicesScrollIndex === dot)} />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Container>

            {/* The Virtoly Advantage */}
            <Container maxWidth="lg" sx={{ py: SECTION_PY, px: CONTAINER_PX }}>
                <Box>
                    <ContentCard sx={{
                        ...getHomePageGlassTileSx(theme),
                        p: { xs: 3, sm: 4, md: 5 },
                        borderRadius: 4,
                    }}>
                        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 } }}>
                            <Typography variant="h4" fontWeight={700}>
                                The Virtoly Advantage
                            </Typography>
                        </Box>

                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Grid container spacing={{ xs: 3, sm: 4 }}>
                                {advantageItems.map((item, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Box sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 2,
                                                    background: alpha(getButtonColor(theme), 0.1),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <item.icon sx={{ color: getButtonColor(theme), fontSize: iconSizeFixed.px24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: fontSizeFixed.rem1125 }}>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                        {item.desc}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Grid container spacing={2}>
                                {advantageItems.map((item, index) => (
                                    <Grid item xs={6} key={index}>
                                        <ContentCard sx={{
                                            ...getHomePageGlassTileSx(theme),
                                            p: 2.5,
                                            borderRadius: 3,
                                            height: '100%',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                ...getHomePageGlassHoverShadowSx(theme, getButtonColor(theme)),
                                            },
                                        }}>
                                            <Box sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                background: alpha(getButtonColor(theme), 0.15),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1.5,
                                            }}>
                                                <item.icon sx={{ color: getButtonColor(theme), fontSize: iconSizeFixed.px22 }} />
                                            </Box>
                                            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: fontSizeFixed.rem095 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: fontSizeFixed.rem075 }}>
                                                {item.descShort}
                                            </Typography>
                                        </ContentCard>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </ContentCard>
                </Box>
            </Container>

            {/* Mobile App Section — same glass tile treatment as Virtoly Advantage / other homepage cards */}
            <Container maxWidth="md" sx={{ py: SECTION_PY, px: CONTAINER_PX }}>
                <Box>
                    <ContentCard
                        sx={{
                            ...getHomePageGlassTileSx(theme),
                            p: { xs: 3, sm: 4, md: 5 },
                            borderRadius: 4,
                        }}
                    >
                        <Typography
                            variant="h4"
                            component="h2"
                            fontWeight={700}
                            sx={{
                                mb: { xs: 1.5, sm: 2.5 },
                                fontSize: { xs: responsiveFontSize.h3.xs, sm: responsiveFontSize.h4.xs, md: responsiveFontSize.h4.sm },
                                lineHeight: { xs: 1.28, sm: 1.22 },
                                color: 'text.primary',
                            }}
                        >
                            Book faster with our mobile app
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                gap: { xs: 1, sm: 2, md: 2.5 },
                                width: '100%',
                                alignItems: { xs: 'stretch', sm: 'center' },
                            }}
                        >
                            <CustomButton
                                variant="contained"
                                size="large"
                                onClick={handleOpenIOSStore}
                                startIcon={<AppleIcon sx={{ fontSize: { xs: iconSizeFixed.px20, sm: iconSizeFixed.px24, md: iconSizeFixed.px28 } }} />}
                                sx={{
                                    flex: { xs: 1, sm: '0 0 auto' },
                                    minWidth: { xs: 0, sm: 'auto' },
                                    px: { xs: 1, sm: 4, md: 5 },
                                    py: { xs: 1.25, sm: 1.5, md: 1.75 },
                                    fontSize: { xs: fontSizeFixed.rem075, sm: fontSizeFixed.rem1, md: fontSizeFixed.rem1125 },
                                    whiteSpace: { xs: 'nowrap' },
                                    minHeight: { sm: 52, md: 56 },
                                    '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1.25, md: 1.5 } },
                                }}
                            >
                                App Store
                            </CustomButton>
                            <CustomButton
                                variant="outlined"
                                size="large"
                                onClick={handleOpenAndroidStore}
                                startIcon={<AndroidIcon sx={{ fontSize: { xs: iconSizeFixed.px20, sm: iconSizeFixed.px24, md: iconSizeFixed.px28 } }} />}
                                sx={{
                                    flex: { xs: 1, sm: '0 0 auto' },
                                    minWidth: { xs: 0, sm: 'auto' },
                                    px: { xs: 1, sm: 4, md: 5 },
                                    py: { xs: 1.25, sm: 1.5, md: 1.75 },
                                    fontSize: { xs: fontSizeFixed.rem075, sm: fontSizeFixed.rem1, md: fontSizeFixed.rem1125 },
                                    whiteSpace: { xs: 'nowrap' },
                                    minHeight: { sm: 52, md: 56 },
                                    '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1.25, md: 1.5 } },
                                }}
                            >
                                Google Play
                            </CustomButton>
                        </Box>
                    </ContentCard>
                </Box>
            </Container>

            {/* Final CTA - Modern Gradient Card */}
            <Box sx={{ py: SECTION_PY, position: 'relative' }}>
                <Container maxWidth="md" sx={{ px: CONTAINER_PX }}>
                    <Box>
                        <ContentCard
                            sx={{
                                ...getHomePageGlassTileSx(theme),
                                p: { xs: 3, sm: 4, md: 5 },
                                borderRadius: 4,
                                textAlign: 'center',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: getButtonColor(theme),
                                        fontWeight: 700,
                                        letterSpacing: 2.5,
                                        fontSize: responsiveFontSize.caption,
                                        mb: 2,
                                        display: 'inline-block',
                                    }}
                                >
                                    GET STARTED TODAY
                                </Typography>

                                <Typography
                                    variant={isMobile ? 'h4' : 'h3'}
                                    fontWeight={800}
                                    sx={{
                                        color: 'text.primary',
                                        mb: 2,
                                        fontSize: responsiveFontSize.h2,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Your next great hire is{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            background: 'linear-gradient(135deg, #FFD54F 0%, #FFB74D 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                        }}
                                    >
                                        one search away.
                                    </Box>
                                </Typography>

                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'text.secondary',
                                        mb: 4,
                                        fontWeight: 400,
                                        fontSize: responsiveFontSize.bodyMdDip,
                                        maxWidth: 600,
                                        mx: 'auto',
                                    }}
                                >
                                    Join thousands of customers and experts already using Virtoly.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <CustomButton
                                        variant="contained"
                                        size="large"
                                        onClick={handleCustomerCta}
                                        sx={{
                                            px: { xs: 4, sm: 5 },
                                            py: { xs: 1.5, sm: 2 },
                                            fontSize: responsiveFontSize.bodyLarge,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Start Your Order
                                    </CustomButton>
                                    <CustomButton
                                        variant="outlined"
                                        size="large"
                                        onClick={handleWorkerCta}
                                        sx={{
                                            px: { xs: 4, sm: 5 },
                                            py: { xs: 1.5, sm: 2 },
                                            fontSize: responsiveFontSize.bodyLarge,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Join as an Expert
                                    </CustomButton>
                                </Box>
                            </Box>
                        </ContentCard>
                    </Box>
                </Container>
            </Box>

            {/* Got questions – help & services links */}
            <Container maxWidth="md" sx={{ py: SECTION_PY, px: CONTAINER_PX }}>
                <ContentCard
                    sx={{
                        ...getHomePageGlassTileSx(theme),
                        p: { xs: 3, sm: 4, md: 5 },
                        borderRadius: 4,
                        overflow: 'hidden',
                        textAlign: 'center',
                        backdropFilter: 'blur(16px) saturate(1.12)',
                        background: `linear-gradient(135deg, ${alpha('#26A69A', theme.palette.mode === 'dark' ? 0.4 : 0.38)} 0%, ${alpha('#1976D2', theme.palette.mode === 'dark' ? 0.3 : 0.28)} 50%, ${alpha('#FF6B35', theme.palette.mode === 'dark' ? 0.24 : 0.2)} 100%)`,
                        border: `1px solid ${alpha('#fff', theme.palette.mode === 'dark' ? 0.32 : 0.4)}`,
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: getButtonColor(theme),
                        },
                    }}
                >
                    <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                        <Typography
                            variant="overline"
                            sx={{
                                color: '#FFD54F',
                                fontWeight: 600,
                                letterSpacing: 3,
                                fontSize: responsiveFontSize.caption,
                                mb: 2,
                                display: 'inline-block',
                            }}
                        >
                            GOT QUESTIONS?
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.88)',
                                mb: 2,
                                maxWidth: 520,
                                mx: 'auto',
                                mt: 0,
                                fontSize: { xs: responsiveFontSize.bodyMedium.xs, sm: responsiveFontSize.bodyLead.sm },
                                lineHeight: 1.55,
                            }}
                        >
                            We’re here to help. See how it works or browse the help center.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                width: '100%',
                                '& > *': {
                                    flex: { xs: '1 1 100%', sm: '0 0 auto' },
                                    minWidth: { xs: 0, sm: 'auto' },
                                },
                            }}
                        >
                            <CustomButton
                                variant="contained"
                                size="medium"
                                onClick={openHowItWorksModal}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    backgroundColor: getButtonColor(theme),
                                    color: theme.palette.getContrastText(getButtonColor(theme)),
                                }}
                            >
                                How It Works
                            </CustomButton>
                            <CustomButton
                                variant="outlined"
                                size="medium"
                                onClick={() => navigate('/help-center')}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    borderColor: 'rgba(255,255,255,0.6)',
                                    color: '#fff',
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderColor: '#fff',
                                        background: 'rgba(255,255,255,0.15)',
                                        borderWidth: 2,
                                    },
                                }}
                            >
                                Visit Help Center
                            </CustomButton>
                        </Box>
                    </Box>
                </ContentCard>
            </Container>

            {deferredModalsReady && (
                <Suspense fallback={null}>
                    <AuthModal
                        open={authModalOpen}
                        onClose={() => setAuthModalOpen(false)}
                        userType="customer"
                        initialMode="signup"
                    />
                    <AuthModal
                        open={workerAuthModalOpen}
                        onClose={() => setWorkerAuthModalOpen(false)}
                        userType="worker"
                        initialMode="signup"
                    />
                    <GetStartedChooser
                        open={getStartedOpen}
                        onClose={() => setGetStartedOpen(false)}
                        onSelect={handleGetStartedSelect}
                        secondaryActionLabel="I'll browse first"
                    />
                    <HowItWorksModal
                        open={howItWorksOpen}
                        onClose={() => setHowItWorksOpen(false)}
                        initialTab={howItWorksModalInitialTab}
                    />
                </Suspense>
            )}

        </Box>
    );
}
